import React, { useState, useEffect } from 'react';
import PanelCard from '../ui/PanelCard';
import InputField from '../Forms/Input/InputField';

const bills = [100, 50, 20, 10, 5, 1];
const coins = [0.25, 0.1, 0.05, 0.01];

const denominations = [...bills, ...coins];

interface Props {
  disabled?: boolean;
  auditStarted?: boolean;
  onChange: (details: any[]) => void;
}

const CashCountDenominationSheet: React.FC<Props> = ({
  disabled,
  auditStarted,
  onChange,
}) => {
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  /**
   * 🧹 Limpiar conteo cuando termina el arqueo
   */
  useEffect(() => {
    if (!auditStarted) {
      setQuantities({});

      const emptyDetails = denominations.map((d) => ({
        denomination: d,
        quantity: 0,
      }));

      onChange(emptyDetails);
    }
  }, [auditStarted]);

  /**
   * 🔄 Actualizar cantidades
   */
  const handleChange = (denomination: number, value: number) => {
    const updated = {
      ...quantities,
      [denomination]: value,
    };

    setQuantities(updated);

    const details = denominations.map((d) => ({
      denomination: d,
      quantity: updated[d] || 0,
    }));

    onChange(details);
  };

  /**
   * 💲 Formatear moneda
   */
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  /**
   * 🧮 Subtotal por denominación
   */
  const getSubtotal = (d: number) => {
    const qty = quantities[d] || 0;
    return d * qty;
  };

  /**
   * ⌨️ Control teclado (solo números)
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(e.key)) return;

    if (e.key === 'Enter' || e.key === 'Escape') {
      e.preventDefault();
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * 🧾 Render fila
   */
  const renderRow = (d: number, type: 'bill' | 'coin') => {
    const subtotal = getSubtotal(d);

    const iconKey = type === 'bill' ? 'bill' : 'coin';

    return (
      <div key={d} className="flex items-end gap-3">
        <div className="flex-1">
          <InputField
            label={`$${d.toFixed(2)}`}
            name={`denomination-${d}`}
            type="number"
            min={0}
            step="1"
            value={quantities[d] || ''}
            iconKey={iconKey}
            rightContent={`= $${formatCurrency(subtotal)}`}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={disabled}
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              const value = e.target.value;

              if (value === '') {
                handleChange(d, 0);
                return;
              }

              const clean = value.replace(/[^0-9]/g, '');
              const parsed = parseInt(clean, 10);

              if (!isNaN(parsed)) {
                handleChange(d, parsed);
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <PanelCard title="Conteo de Efectivo">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* BILLETES */}
        <div>
          <h4 className="mb-3 font-semibold text-black dark:text-white">
            Billetes
          </h4>

          <div className="space-y-3">
            {bills.map((d) => renderRow(d, 'bill'))}
          </div>
        </div>

        {/* MONEDAS */}
        <div>
          <h4 className="mb-3 font-semibold text-black dark:text-white">
            Monedas
          </h4>

          <div className="space-y-3">
            {coins.map((d) => renderRow(d, 'coin'))}
          </div>
        </div>
      </div>
    </PanelCard>
  );
};

export default CashCountDenominationSheet;
